import React, { useState, forwardRef, useImperativeHandle, useEffect } from 'react';
import OlcButton from '~/components/General/OlcButton';
import OlcFormModal from '~/components/General/OlcFormModal';

// Third Party Libraries
import DatePicker from '~/components/General/OlcDatePicker';

// Hooks
import { useDispatch, useSelector } from 'react-redux';

// Actions
import {
  CLEAR_ADMIN_LOOKUPS_FILTERS,
  SET_ADMIN_LOOKUPS_FILTERS,
} from '~/redux/actions/action-types.js';
import { failure } from '~/redux/actions/snackbar-actions';

// Utils
import { MESSAGES } from '~/utils/message';
import {
  dateProtector,
  handleRawChangeForDatePicker,
  formatDateOnly,
  parseLocalDateOnly,
} from '~/utils/date-format.js';
import { DATE_REGEX, DEFAULT_PAGINATION } from '~/utils/constants';

// Mui components
import Typography from '@mui/material/Typography';
import { Box, Grid, Input, InputLabel } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

// Styles
import './styles.scss';

const LABELS = MESSAGES.ADMIN_DASHBOARD.LOOKUPS.ADVANCE_FILTERS;

const AdvanceFilters = forwardRef((props, ref) => {
  const { handleCloseFilter, open, updatePagination } = props;
  const [orderId, setOrderId] = useState('');
  const [orderItemId, setOrderItemId] = useState('');
  const [batchId, setBatchId] = useState('');
  const [isDatepickerOpen, setIsDatepickerOpen] = useState(false);
  const [mailedDate, setMailedDate] = useState([null, null]);
  const [startDateMailed, endDateMailed] = mailedDate;

  const dispatch = useDispatch();

  const perPage = useSelector(
    (state) => state.adminLookupsReducers.perPage || DEFAULT_PAGINATION.pageSize
  );

  const filters = useSelector((state) => state.adminLookupsReducers.filters);

  const resetFilterValues = () => {
    setOrderId('');
    setOrderItemId('');
    setBatchId('');
    setMailedDate([null, null]);
  };

  const onClose = () => {
    clearFilters(true);
  };

  const clearFilters = (persist = false) => {
    resetFilterValues();
    !persist ? dispatch({ type: CLEAR_ADMIN_LOOKUPS_FILTERS }) : undefined;
    handleCloseFilter();
  };

  const applyFilters = () => {
    if (startDateMailed && endDateMailed === null) {
      dispatch(failure(MESSAGES.ORDERS.END_DATE_REQUIRED));
      return;
    }

    const dateRangeMailed =
      startDateMailed && endDateMailed
        ? {
            startDate: formatDateOnly(startDateMailed),
            endDate: formatDateOnly(endDateMailed),
          }
        : null;

    let payload = {
      applyFilters: true,
      filters: {
        orderId: orderId || null,
        orderItemId: orderItemId || null,
        batchId: batchId || null,
        mailedDate: dateRangeMailed,
      },
    };

    const filtersWithValues = Object.keys(payload.filters).filter(
      (key) => payload.filters[key] !== null
    );

    payload.filterSize = filtersWithValues.length;

    updatePagination({
      pageSize: perPage,
      page: 1,
    });

    if (filtersWithValues.length <= 0) {
      dispatch(failure(MESSAGES.ORDERS.FILTERS_REQUIRED));
      return;
    }

    dispatch({ type: SET_ADMIN_LOOKUPS_FILTERS, payload: payload });
    onClose();
  };

  useImperativeHandle(ref, () => ({
    clearFilters() {
      clearFilters();
    },
  }));

  useEffect(() => {
    if (Object.values(filters).length) {
      setOrderId(filters.orderId || '');
      setOrderItemId(filters.orderItemId || '');
      setBatchId(filters.batchId || '');
      filters.mailedDate
        ? setMailedDate([
            parseLocalDateOnly(filters?.mailedDate?.startDate),
            parseLocalDateOnly(filters?.mailedDate?.endDate),
          ])
        : undefined;
    } else {
      resetFilterValues();
    }
  }, [open]);

  return (
    <OlcFormModal
      open={open}
      onClose={onClose}
      title={LABELS.HEADING}
      className="lookupsFilterModalWrapper olcAdminFilterModal"
      contentClassName="filterModalContent"
      footer={
        <>
          <OlcButton variant="secondary" onClick={() => clearFilters()}>
            {LABELS.CANCEL_BUTTON}
          </OlcButton>
          <OlcButton variant="primary" onClick={applyFilters}>
            {LABELS.SUBMIT_BUTTON}
          </OlcButton>
        </>
      }
    >
      <Grid container className="adminLookupsFilterGrid">
        <Grid item lg={6} md={6} sm={12} xs={12}>
          <Box className="inputWrapper">
            <Typography>{LABELS.ORDER_ID_LABEL}</Typography>
            <Input
              className="createdByInput"
              value={orderId}
              name="orderId"
              autoComplete="off"
              onChange={(e) => setOrderId(e.target.value.replace(/\D/g, ''))}
              type="text"
              placeholder={LABELS.ORDER_ID_PLACEHOLDER}
            />
          </Box>
        </Grid>
        <Grid item lg={6} md={6} sm={12} xs={12}>
          <Box className="inputWrapper">
            <Typography>{LABELS.ORDER_ITEM_ID_LABEL}</Typography>
            <Input
              className="createdByInput"
              value={orderItemId}
              name="orderItemId"
              autoComplete="off"
              onChange={(e) => setOrderItemId(e.target.value.replace(/\D/g, ''))}
              type="text"
              placeholder={LABELS.ORDER_ITEM_ID_PLACEHOLDER}
            />
          </Box>
        </Grid>
        <Grid item lg={6} md={6} sm={12} xs={12}>
          <Box className="inputWrapper">
            <Typography>{LABELS.BATCH_ID_LABEL}</Typography>
            <Input
              className="createdByInput"
              value={batchId}
              name="batchId"
              autoComplete="off"
              onChange={(e) => setBatchId(e.target.value.replace(/\D/g, ''))}
              type="text"
              placeholder={LABELS.BATCH_ID_PLACEHOLDER}
            />
          </Box>
        </Grid>
        <Grid item lg={6} md={6} sm={12} xs={12}>
          <Box className="mailedDate">
            <Box className="selectDate">
              <InputLabel className="dateLabel">{LABELS.MAILED_DATE_LABEL}</InputLabel>
              <KeyboardArrowDownIcon
                sx={{
                  color: isDatepickerOpen ? '#ED5C2F' : 'inherit',
                }}
              />
              <DatePicker
                onCalendarClose={() => setIsDatepickerOpen(false)}
                selectsRange={true}
                startDate={startDateMailed}
                endDate={endDateMailed}
                onChange={(update) => {
                  dateProtector(update);
                  setMailedDate(update);
                }}
                minDate={null}
                onChangeRaw={(e) => {
                  if (e.target.value === '' || DATE_REGEX.test(e.target.value)) {
                    handleRawChangeForDatePicker(e, setMailedDate);
                  } else {
                    e.preventDefault();
                  }
                }}
                onFocus={() => setIsDatepickerOpen(true)}
                onBlur={(e) => {
                  if (e.target.value == '') {
                    setMailedDate([null, null]);
                  }
                  setIsDatepickerOpen(false);
                }}
                placeholderText={LABELS.DATE_PLACEHOLDER}
                isClearable
              />
            </Box>
          </Box>
        </Grid>
      </Grid>
    </OlcFormModal>
  );
});

AdvanceFilters.displayName = 'LookupsAdvanceFilters';

export default AdvanceFilters;
